import '../../../scss/common/components/planner-selection.scss'

import {
  Box,
  Container,
  Display4,
  Grid,
  GridItem,
} from '@sainsburys-tech/fable'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  clearLocalStorePlanners,
  setLocalStorePlanners,
} from '../../../utils/localPlanner'
import { getUserStore } from '../../../utils/localStore'
import { getUserPreferredPlanner } from '../../api/plannerApi'
import { getStoreCounters } from '../../api/storeApi'
import { useApiClient } from '../../AppContext/appContext'
import { ErrorMessage } from '../../enums/ErrorMessage'
import { PreferredPlanner } from '../../enums/PreferredPlannerEnum'
import useErrorHandler from '../../hooks/useErrorHandler'
import { IStorePlanners } from '../../types/IStorePanners'
import ProgressLoading from '../ProgressLoading'
import BakeryIcon from './PlannerIcons/Bread.svg'
import HotFoodIcon from './PlannerIcons/HotFood.svg'
import PlannerSelectionCard from './PlannerSelectionCard'
import ProductionPlanningHeader from './ProductionPlanningHeader'

const PlannerSelection = () => {
  const apiClient = useApiClient()
  const location = useLocation()
  const navigate = useNavigate()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [storePlanners, setStorePlanners] = useState<IStorePlanners>({
    count: 0,
    hasBakery: false,
    hasHotFood: false,
  })

  const queryParams = location.search

  const { handleError, resetError } = useErrorHandler(setErrorMessage)
  const isLoading = isDataLoading

  const sendToUsersPlanner = useCallback(
    (preferredPlanner: number, planners: IStorePlanners) => {
      if (planners.count === 0) {
        clearLocalStorePlanners()
        handleError('No preferred planner found')
        return
      }

      // back to planner selection screen if preferred planner is not available from a selected store
      if (
        (preferredPlanner === (PreferredPlanner.Bakery as number) &&
          !planners.hasBakery) ||
        (preferredPlanner === (PreferredPlanner.HotFood as number) &&
          !planners.hasHotFood) ||
        queryParams === '?reset=true'
      ) {
        preferredPlanner = PreferredPlanner.None
      }

      switch (preferredPlanner) {
        case PreferredPlanner.None:
          if (planners.count > 1) {
            return
          }
          if (planners.hasBakery) {
            navigate('/bakeryview')
          } else {
            navigate('/hotfoodview')
          }
          break

        case PreferredPlanner.Bakery:
          navigate('/bakeryview')
          break

        case PreferredPlanner.HotFood:
          navigate('/hotfoodview')
          break

        default:
          handleError('No preferred planner to move')
          break
      }
    },
    [navigate, queryParams, handleError],
  )

  useEffect(() => {
    const loadStoreData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [preferredPlanner, storeCounters] = await Promise.all([
          getUserPreferredPlanner(apiClient),
          getStoreCounters(apiClient, store.storeId),
        ])

        // updated planners based on the StorePlanners
        const planners = {
          count:
            (storeCounters.hasBakery ? 1 : 0) +
            (storeCounters.hasHotFood ? 1 : 0),
          hasBakery: storeCounters.hasBakery,
          hasHotFood: storeCounters.hasHotFood,
        }

        setStorePlanners(planners) // used in this component (in the return statement)
        setLocalStorePlanners(planners) // Used by the header

        sendToUsersPlanner(preferredPlanner, planners)
      } catch (e) {
        handleError(ErrorMessage.GeneralException, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadStoreData()
  }, [apiClient, store, handleError, resetError, sendToUsersPlanner])

  useEffect(() => {
    if (!isLoading && errorMessage) {
      navigate('/error')
    }
  }, [isLoading, errorMessage, navigate])

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      <ProductionPlanningHeader
        headerMessage={'Production Planning'}
        className='production-planning-header'
      />

      {!isLoading && (
        <>
          <Box padding={{ md: 'md' }} backgroundColor='primary'>
            <Display4 className='planner-selection-heading'>
              Choose your planner
            </Display4>
            <Container size='lg'>
              <Grid cols={12} gap='lg' className='planner-selection-grid'>
                {storePlanners.hasBakery && (
                  <GridItem colSpan={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <PlannerSelectionCard
                      {...{
                        disabled: false,
                        linkPath: '/bakeryview',
                        plannerIcon: BakeryIcon,
                        plannerName: 'Bakery Planner',
                        plannerType: PreferredPlanner.Bakery,
                      }}
                    />
                  </GridItem>
                )}
                {storePlanners.hasHotFood && (
                  <GridItem colSpan={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <PlannerSelectionCard
                      {...{
                        disabled: false,
                        linkPath: '/hotfoodview',
                        plannerIcon: HotFoodIcon,
                        plannerName: 'Hot Food Temp Control',
                        plannerType: PreferredPlanner.HotFood,
                      }}
                    />
                  </GridItem>
                )}
              </Grid>
            </Container>
          </Box>
        </>
      )}
    </>
  )
}

export default PlannerSelection
